/**
 * E2 — GDPR-compliant consent block.
 *
 * Variants:
 *   - standard   full block: privacy notice + marketing checkbox + reassurance line
 *   - condensed  privacy notice + marketing checkbox (no reassurance)
 *   - minimal    privacy notice only (no checkbox; marketing-consent props ignored)
 *   - notice     standard privacy notice only (no checkbox)
 *   - referral   funding-introduction notice + marketing checkbox, and no
 *                "we never share" reassurance, because these enquiries can be
 *                passed to a finance provider (Merchant Cash Advance, WEB-009)
 *
 * The Privacy Policy link target is /privacy-policy (existing site route).
 * The marketing checkbox defaults unchecked (caller owns the boolean state)
 * and never gates form submission.
 */
import { Link } from "react-router-dom";

const PRIVACY_LINK = "/privacy-policy";

const COPY = {
  standardPrivacyPrefix: "We'll use your details to respond to your enquiry and provide any information you've requested. Your data is handled securely in line with our ",
  condensedPrivacyPrefix: "We'll respond to your enquiry. See our ",
  minimalPrivacyPrefix: "We'll email your results. See our ",
  privacyLinkText: "Privacy Policy",
  privacyTrailing: ".",
  marketingLabel: "I'd like to receive useful insights, cost-saving tips, and occasional offers from CanDo Payments.",
  reassurance: "No spam. We never share your details. You can unsubscribe at any time.",
  // Gary's approved wording, 4 Oct 2026 (WEB-009). Exact text; the link sits
  // on "Privacy Policy".
  referralPrivacyPrefix: "We'll use your details to respond to your enquiry. With your agreement, we can pass the details needed for a funding introduction to a third-party finance provider we work with. You can also apply directly to the provider. Read our ",
  referralPrivacyTrailing: " for more about how we handle personal data.",
};

function PrivacyParagraph({ prefix, trailing = COPY.privacyTrailing, testIdPrefix }) {
  return (
    <p
      className="text-xs text-brand-grey leading-relaxed"
      data-testid={testIdPrefix ? `${testIdPrefix}-privacy-notice` : "consent-privacy-notice"}
    >
      {prefix}
      <Link
        to={PRIVACY_LINK}
        className="text-brand-aqua-dark hover:text-brand-aqua underline underline-offset-2"
      >
        {COPY.privacyLinkText}
      </Link>
      {trailing}
    </p>
  );
}

function MarketingCheckbox({ marketingConsent, onMarketingConsentChange, testIdPrefix }) {
  return (
    <label className="flex items-start gap-2.5 cursor-pointer">
      <input
        type="checkbox"
        checked={Boolean(marketingConsent)}
        onChange={(e) => onMarketingConsentChange && onMarketingConsentChange(e.target.checked)}
        className="mt-0.5 w-4 h-4 rounded border-brand-light-grey text-brand-aqua focus:ring-brand-aqua/30"
        data-testid={testIdPrefix ? `${testIdPrefix}-marketing-consent` : "consent-marketing"}
      />
      <span className="text-xs text-brand-grey leading-relaxed">{COPY.marketingLabel}</span>
    </label>
  );
}

function ReassuranceLine({ testIdPrefix }) {
  return (
    <p
      className="text-xs text-brand-grey leading-relaxed"
      data-testid={testIdPrefix ? `${testIdPrefix}-reassurance` : "consent-reassurance"}
    >
      {COPY.reassurance}
    </p>
  );
}

export default function ConsentBlock({
  variant = "standard",
  marketingConsent = false,
  onMarketingConsentChange,
  testIdPrefix,
  className = "",
}) {
  if (variant === "notice") {
    // Standard privacy wording with no marketing checkbox — for forms whose
    // brief rules out a marketing opt-in (e.g. campaign landing pages).
    return (
      <div
        className={`pt-2 ${className}`}
        data-testid={testIdPrefix ? `${testIdPrefix}-consent-block` : "consent-block"}
        data-variant="notice"
      >
        <PrivacyParagraph prefix={COPY.standardPrivacyPrefix} testIdPrefix={testIdPrefix} />
      </div>
    );
  }

  if (variant === "referral") {
    return (
      <div
        className={`space-y-2.5 pt-2 ${className}`}
        data-testid={testIdPrefix ? `${testIdPrefix}-consent-block` : "consent-block"}
        data-variant="referral"
      >
        <PrivacyParagraph prefix={COPY.referralPrivacyPrefix} trailing={COPY.referralPrivacyTrailing} testIdPrefix={testIdPrefix} />
        <MarketingCheckbox
          marketingConsent={marketingConsent}
          onMarketingConsentChange={onMarketingConsentChange}
          testIdPrefix={testIdPrefix}
        />
      </div>
    );
  }

  if (variant === "minimal") {
    return (
      <div
        className={`pt-2 ${className}`}
        data-testid={testIdPrefix ? `${testIdPrefix}-consent-block` : "consent-block"}
        data-variant="minimal"
      >
        <PrivacyParagraph prefix={COPY.minimalPrivacyPrefix} testIdPrefix={testIdPrefix} />
      </div>
    );
  }

  if (variant === "condensed") {
    return (
      <div
        className={`space-y-2 pt-2 ${className}`}
        data-testid={testIdPrefix ? `${testIdPrefix}-consent-block` : "consent-block"}
        data-variant="condensed"
      >
        <PrivacyParagraph prefix={COPY.condensedPrivacyPrefix} testIdPrefix={testIdPrefix} />
        <MarketingCheckbox
          marketingConsent={marketingConsent}
          onMarketingConsentChange={onMarketingConsentChange}
          testIdPrefix={testIdPrefix}
        />
      </div>
    );
  }

  // standard (default)
  return (
    <div
      className={`space-y-2.5 pt-2 ${className}`}
      data-testid={testIdPrefix ? `${testIdPrefix}-consent-block` : "consent-block"}
      data-variant="standard"
    >
      <PrivacyParagraph prefix={COPY.standardPrivacyPrefix} testIdPrefix={testIdPrefix} />
      <MarketingCheckbox
        marketingConsent={marketingConsent}
        onMarketingConsentChange={onMarketingConsentChange}
        testIdPrefix={testIdPrefix}
      />
      <ReassuranceLine testIdPrefix={testIdPrefix} />
    </div>
  );
}
